// Ported from Stephanet's MissedDaysLeave.jsx (Missed Days Management).
import React, { useEffect, useMemo, useState } from 'react'
import { CalendarCheck, CircleCheckBig, Info, ListOrdered, Play, Search, UserRound, Users, Zap } from 'lucide-react'
import * as hr from '../api'
import Loader from '../components/Loader'
import { useHR } from '../HRContext'
import Modal from '../components/Modal'
import { Select } from '../components/Controls'
import { fmtNumber } from '../format'
import { Chip, Kpi, STATUS_TONE } from './missed/shared'

// Leave Assignment Control: current balances, automatic priority-based assignment and manual assignment of missed days to leave

const COLS = ['Sick', 'Casual', 'Annual', 'Maternity', 'Paternity', 'Compassionate']
const BAL_TYPES = ['Sick', 'Casual', 'Annual', 'Maternity', 'Paternity', 'Compassionate']
const label = t => (t === 'Deduction' ? 'Salary Deduction' : `${t} Leave`)
const bal = v => (v === null || v === undefined ? '—' : fmtNumber(v))
const tone = v => (v === null || v === undefined ? 'na' : v > 0 ? 'ok' : 'zero')

export default function MissedDaysLeave() {
  const { notify, refreshBadges } = useHR()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [empId, setEmpId] = useState('')
  const [picked, setPicked] = useState(new Set())
  const [type, setType] = useState('Sick')
  const [note, setNote] = useState('')
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [plan, setPlan] = useState(null)
  const [tick, setTick] = useState(0)

  const load = () => hr.mdLeave().then(d => { setData(d); setError(''); setTick(t => t + 1); return d }).catch(e => setError(e.message))
  useEffect(() => { load() }, [])

  const emp = data?.rows.find(r => String(r.employee_id) === String(empId))
  useEffect(() => { // a new employee starts with all their pending days ticked and the suggested leave type
    setPicked(new Set((emp?.pending || []).map(p => p.id)))
    if (emp?.pending?.length) setType(['Deduction', undefined, null].includes(emp.pending[0].suggested) ? 'Sick' : emp.pending[0].suggested)
  }, [empId, tick]) // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    if (!data) return []
    const s = q.trim().toLowerCase()
    return s ? data.rows.filter(r => [r.name, r.employee_no].some(v => String(v || '').toLowerCase().includes(s))) : data.rows
  }, [data, q])

  if (error && !data) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>
  if (!data) return <Loader title="Loading leave assignment…" subtitle="Fetching balances and pending days" />

  const can = data.can.assign
  const chosen = (emp?.pending || []).filter(p => picked.has(p.id))
  const need = chosen.reduce((s, p) => s + (p.days || 1), 0)
  const avail = emp && BAL_TYPES.includes(type) ? emp.balances[type] : null
  const short = emp && BAL_TYPES.includes(type) && (avail === null || avail === undefined || avail < need)
  const ready = can && emp && chosen.length && note.trim() && !short && !busy && !(type === 'Sick' && data.rules?.sick_attachment && !file)
  const withPending = data.rows.filter(r => r.pending.length)

  const afterRun = (r, what) => {
    if (r.failed?.length) notify(`${r.done} ${what}; ${r.failed.length} could not be assigned: ${r.failed[0]}`, 'bad')
    else notify(`${r.done} missed day(s) ${what}`)
    refreshBadges?.()
    return load()
  }
  const preview = () => {
    setBusy(true)
    hr.mdLeaveAuto(true).then(setPlan).catch(e => notify(e.message, 'bad')).finally(() => setBusy(false))
  }
  const runAuto = () => {
    setBusy(true)
    hr.mdLeaveAuto(false)
      .then(r => { setPlan(null); return afterRun(r, 'assigned to leave automatically') })
      .catch(e => notify(e.message, 'bad')).finally(() => setBusy(false))
  }
  const assign = e => {
    e.preventDefault()
    if (!ready) return
    setBusy(true)
    const ids = chosen.map(p => p.id)
    const call = type === 'Sick' && file ? hr.mdAssignWithDocument(ids, type, note.trim(), file)
      : hr.mdLeaveAssign({ employee_id: emp.employee_id, ids, leave_type: type, note: note.trim() })
    call.then(r => { setNote(''); setFile(null); return afterRun(r, `approved and assigned to ${label(type)}`) })
      .catch(err => notify(err.message, 'bad')).finally(() => setBusy(false))
  }
  const toggle = id => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n })

  return (
    <div className="mdr mds mdl">
      <div className="mdr-top">
        <div>
          <h2>Leave Assignment Control</h2>
          <p>Assign missed days to leave entitlements — manual or automatic priority-based assignment</p>
        </div>
      </div>

      <div className="mdr-card mdr-table-card">
        <div className="mds-table-head">
          <b>Current Leave Balances <span className="mdl-year">{data.year} · available days</span></b>
          <div className="mdr-search mds-quick no-print"><Search size={14} /><input placeholder="Search employee..." value={q} onChange={e => setQ(e.target.value)} /></div>
        </div>
        <div className="mdr-scroll mdl-scroll">
          <table className="mdr-table mds-table">
            <thead>
              <tr><th>Employee ID</th><th>Employee Name</th>{COLS.map(c => <th key={c} className="c">{label(c)}</th>)}<th className="c">Pending Missed Days</th></tr>
            </thead>
            <tbody key={tick}>
              {rows.map((r, i) => (
                <tr key={r.employee_id} className={`mdr-row ${String(r.employee_id) === String(empId) ? 'picked' : ''}`} style={{ animationDelay: `${Math.min(i, 20) * 18}ms` }}>
                  <td className="mdr-emp">{r.employee_no}</td>
                  <td className="mdr-name">{r.name}</td>
                  {COLS.map(c => <td key={c} className={`c mdl-bal ${tone(r.balances[c])}`}>{bal(r.balances[c])}</td>)}
                  <td className="c">
                    {r.pending.length
                      ? <button className="mdl-pend" disabled={!can} onClick={() => setEmpId(String(r.employee_id))} title="Assign these in Manual Leave Assignment">{r.pending.length} pending</button>
                      : <span className="mds-dash">—</span>}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={COLS.length + 3} className="mdr-empty">No employees match.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mdl-grid">
        <div className="mdr-card mdl-card">
          <div className="mds-card-title"><Zap size={16} /> Automatic Leave Assignment</div>
          <div className="mdl-rule">
            <span><ListOrdered size={14} /> Assignment Priority Rule:</span>
            <ol>{data.priority.map(t => <li key={t}>{label(t)}</li>)}</ol>
            <small>Each pending missed day goes to the first leave type with enough balance left; Unpaid Leave when none has.</small>
          </div>
          <div className="md-kpis mds-kpis mdl-kpis">
            <Kpi label="Pending Missed Days" value={data.pending} Ico={CalendarCheck} tone="amber" note="Open for assignment" />
            <Kpi label="Employees Affected" value={data.employees_affected} Ico={Users} tone="blue" note="With pending days" i={1} />
          </div>
          <button className="mds-run mdl-run" disabled={!can || !data.pending || busy} onClick={preview}><Play size={14} /> Run Auto Assignment</button>
          {!can && <small className="muted">You need approve access to assign leave.</small>}
        </div>

        <form className="mdr-card mdl-card" onSubmit={assign}>
          <div className="mds-card-title"><UserRound size={16} /> Manual Leave Assignment</div>
          <label className="mdl-field">Select Employee *
            <Select value={empId} onChange={e => setEmpId(e.target.value)}>
              <option value="">Choose employee...</option>
              {withPending.map(r => <option key={r.employee_id} value={r.employee_id}>{`${r.employee_no} — ${r.name} (${r.pending.length} pending)`}</option>)}
              {data.rows.filter(r => !r.pending.length).map(r => <option key={r.employee_id} value={r.employee_id}>{`${r.employee_no} — ${r.name}`}</option>)}
            </Select>
          </label>

          {emp && <>
            <div className="mdl-sub">Leave Balances for {emp.name}</div>
            <div className="mdl-bals">
              {['Sick', 'Casual', 'Annual'].map(t => <div key={t} className={`mdl-bal-tile ${tone(emp.balances[t])}`}><small>{t}</small><b>{bal(emp.balances[t])}</b></div>)}
            </div>
            <div className="mdl-sub">Select Missed Days to Assign</div>
            <div className="mdl-days">
              {emp.pending.map(p => (
                <label key={p.id} className={`mdl-day ${picked.has(p.id) ? 'on' : ''}`}>
                  <input type="checkbox" checked={picked.has(p.id)} onChange={() => toggle(p.id)} />
                  <span><b>{p.date}</b> — {p.weekday} <em className="mdr-id">{p.reference}</em></span>
                  <Chip tone={STATUS_TONE[p.status]}>{p.status}</Chip>
                </label>
              ))}
              {!emp.pending.length && <div className="mdr-empty">No pending missed days for this employee.</div>}
            </div>
            <small className="mdl-count">{chosen.length} day(s) selected</small>
          </>}

          <label className="mdl-field">Leave Type *
            <Select value={type} onChange={e => setType(e.target.value)}>
              {data.leave_types.map(t => <option key={t} value={t}>{label(t)}{emp && BAL_TYPES.includes(t) ? ` (${bal(emp.balances[t])} available)` : ''}</option>)}
            </Select>
          </label>
          {short && chosen.length > 0 && <div className="mdl-warn">{emp.name} has {bal(avail)} {label(type)} day(s) available — not enough for {fmtNumber(need)} day(s). Pick another leave type.</div>}
          {type === 'Sick' && (
            <label className="mdl-field">Medical Document {data.rules?.sick_attachment ? '*' : ''}
              <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={e => setFile(e.target.files[0] || null)} />
              <small className="muted">{data.rules?.sick_attachment ? 'Require Attachment for Sick Leave is on — attach the medical document.'
                : data.rules?.medical_after_1 ? 'Needed when this makes more than one consecutive sick day (Require Medical Report After 1 Sick Day).' : 'Optional.'}</small>
            </label>
          )}
          <label className="mdl-field">Comment / Reason *
            <textarea rows={3} placeholder="Enter reason for leave assignment..." value={note} onChange={e => setNote(e.target.value)} />
          </label>
          <button type="submit" className="mds-run mdl-run" disabled={!ready}><CircleCheckBig size={14} /> {busy ? 'Assigning…' : 'Approve + Assign Leave'}</button>
        </form>
      </div>

      {plan && (
        <Modal className="mdl-modal" title={<><Zap size={18} /> Run Auto Assignment</>} onClose={() => setPlan(null)}>
          <div className="mdl-modal-body">
            <p className="mdl-modal-lead">The pending missed days below will be <b>approved</b> and <b>assigned to leave</b> by the priority rule.</p>
            <div className="md-kpis mds-kpis mdl-kpis">
              <Kpi label="Pending Missed Days" value={plan.pending} Ico={CalendarCheck} tone="amber" note="To be assigned" />
              <Kpi label="Employees Affected" value={plan.employees} Ico={Users} tone="blue" note="With pending days" i={1} />
            </div>
            <div className="mdl-sub">Assignment by Leave Type <span className="mdl-year">in priority order</span></div>
            <div className="mdl-plan">
              {data.priority.map((t, i) => {
                const n = plan.plan[t] || 0
                return (
                  <div key={t} className={n ? 'on' : ''} style={{ animationDelay: `${i * 50}ms` }}>
                    <span className="mdl-step">{i + 1}</span>
                    <div className="mdl-plan-main">
                      <div><span>{label(t)}</span><b>{n} day(s)</b></div>
                      <i><em style={{ width: `${plan.pending ? (n / plan.pending) * 100 : 0}%` }} /></i>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="mdl-info"><Info size={15} /> Each assignment books an approved one-day leave and reduces the balance. You can reverse it from Review &amp; Approval.</div>
            <div className="mdh-form-foot">
              <button type="button" className="mdr-btn" onClick={() => setPlan(null)}>Cancel</button>
              <button type="button" className="mds-run" disabled={busy} onClick={runAuto}><Zap size={14} /> {busy ? 'Assigning…' : `Assign All (${plan.pending})`}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
